import { Box, FormControlLabel, Radio } from '@customink/pigment-react';
import { kebabCase } from 'lodash';
import type { ChangeEventHandler, ReactNode } from 'react';

import type { DeliveryMethod } from './util';

import { colorSmoke, spacing8 } from '@/utils/designTokens';

type DeliveryOptionProps = {
  label: ReactNode;
  deliveryMethod: DeliveryMethod;
  selected: boolean;
  disabled?: boolean;
  children?: ReactNode;
  onSelect: ChangeEventHandler;
};

const border = {
  selected: { border: `1px solid #1e39d2` },
  unselected: { border: `1px solid ${colorSmoke}` },
};

export default function DeliveryOption({
  label,
  deliveryMethod,
  selected,
  onSelect,
  disabled,
  children,
}: DeliveryOptionProps) {
  // Clicking anywhere in the box selects the radio button
  const handleBoxClick = () => {
    if (!disabled && !selected) {
      const syntheticEvent = {
        target: { value: deliveryMethod },
        currentTarget: { value: deliveryMethod },
      } as React.ChangeEvent<HTMLInputElement>;

      onSelect(syntheticEvent);
    }
  };

  return (
    <Box
      data-testid={`delivery-option-${kebabCase(deliveryMethod)}`}
      onClick={handleBoxClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: spacing8,
        padding: '7px 16px 7px',
        ...(selected ? border.selected : border.unselected),
        cursor: disabled || selected ? 'default' : 'pointer',
      }}
    >
      <FormControlLabel
        checked={selected}
        control={
          <Radio
            data-testid="option-radio"
            disabled={disabled}
            onChange={onSelect}
          />
        }
        label={label}
        multiline
        value={deliveryMethod}
      />
      {!!selected && children && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            padding: '0 0 0.25rem 1.625rem',
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
}
